/**
 * UsefulInfoSidebar Component
 * Left sidebar with Weather, Calendar, Messages, Green Stay preferences
 * Phase D4.2: Updated with scheduled preferences
 */

import { useState, useEffect, useRef, forwardRef } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import {
  Sun,
  Cloud,
  CloudRain,
  Calendar,
  Mail,
  Leaf,
  Wrench,
  Sparkles,
} from 'lucide-react';
import { useKeyboardNavigation } from '@/hooks';
import { useWeather } from '@/hooks';
import { API_BASE } from '@/config/api';
import {
  getNextEvent,
  formatEventTime,
  mockMessages,
} from '@/data/mockHotelData';
import { getReadIds } from '@/services/messageReadStore';
import type { Booking } from '@/types';
import { BookingStatus } from '@/types';

interface UsefulInfoSidebarProps {
  enabled: boolean;
  greenStayEnabled: boolean;
  skipCleaningDays: string[];    // Phase D4.2: ISO dates
  towelsOnlyDays: string[];      // Phase D4.2: ISO dates
  stayCheckIn: string;           // Phase D4.2: ISO date
  stayCheckOut: string;          // Phase D4.2: ISO date
  showGreenStayToast?: boolean;
  greenStayToastMessage?: string;
  onGreenStayToggle?: () => void;
  onSkipCleaningChange?: (days: string[]) => void;  // Phase D4.2
  onTowelsOnlyChange?: (days: string[]) => void;    // Phase D4.2
  onCalendarClick?: () => void;
  onMessagesClick?: () => void;
  onWeatherClick?: () => void;
  onRequestsClick?: () => void;  // Phase P2.2-9
  onGreenStayClick?: () => void;  // Phase P2.2-10
  viewIsOpen?: boolean;  // Disable keyboard nav when a view is open
  bookings?: Booking[];  // Real bookings for the calendar widget
  /** P2.8-4b: D-Pad boundary routing — right from sidebar → main zone */
  onRightBoundary?: () => void;
}

const SIDEBAR_ITEMS = [
  'weather',
  'calendar',
  'requests',  // Phase P2.2-9: My Requests
  'messages',
  'greenStay',  // Green Stay section handles its own navigation when focused
] as const;

function WeatherIcon({ condition }: { condition: string }) {
  switch (condition) {
    case 'sunny':
      return <Sun className="w-8 h-8 text-yellow-400" />;
    case 'cloudy':
      return <Cloud className="w-8 h-8 text-gray-400" />;
    case 'rainy':
      return <CloudRain className="w-8 h-8 text-blue-400" />;
    default:
      return (
        <div className="relative">
          <Sun className="w-8 h-8 text-yellow-400" />
          <Cloud className="w-5 h-5 text-gray-300 absolute -bottom-1 -right-1" />
        </div>
      );
  }
}

export function UsefulInfoSidebar({
  enabled,
  greenStayEnabled,
  skipCleaningDays,
  towelsOnlyDays,
  stayCheckIn: _stayCheckIn,
  stayCheckOut: _stayCheckOut,
  showGreenStayToast: _showGreenStayToast,
  greenStayToastMessage: _greenStayToastMessage,
  onGreenStayToggle: _onGreenStayToggle,
  onSkipCleaningChange: _onSkipCleaningChange,
  onTowelsOnlyChange: _onTowelsOnlyChange,
  onCalendarClick,
  onMessagesClick,
  onWeatherClick,
  onRequestsClick,  // Phase P2.2-9
  onGreenStayClick,  // Phase P2.2-10
  viewIsOpen = false,
  bookings,
  onRightBoundary,
}: UsefulInfoSidebarProps) {
  // Track if we're inside the Green Stay section (keyboard nav handed off)
  const insideGreenStay = false;
  const { weather: liveWeather } = useWeather();

  // Reactive unread count — recomputes whenever the guest reads a message
  const [unreadMockCount, setUnreadMockCount] = useState(() => {
    const read = getReadIds();
    return mockMessages.filter(m => !m.read && !read.has(m.id)).length;
  });

  // Ledger messages (staff-sent) — polled independently so the badge shows
  // new messages even when the inbox is closed.
  const ledgerIdsRef = useRef<string[]>([]);
  const [ledgerUnreadCount, setLedgerUnreadCount] = useState(0);

  function recountLedger() {
    const read = getReadIds();
    setLedgerUnreadCount(ledgerIdsRef.current.filter(id => !read.has(id)).length);
  }

  useEffect(() => {
    async function pollLedger() {
      try {
        const res = await fetch(`${API_BASE}/activity-ledger?type=MESSAGE&limit=50`);
        if (!res.ok) return;
        const data = await res.json();
        ledgerIdsRef.current = (data.activities ?? []).map((r: any) => String(r.id));
        recountLedger();
      } catch { /* non-fatal */ }
    }
    pollLedger();
    const timer = setInterval(pollLedger, 30_000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unreadCount = unreadMockCount + ledgerUnreadCount;

  useEffect(() => {
    function recountMock() {
      const read = getReadIds();
      setUnreadMockCount(mockMessages.filter(m => !m.read && !read.has(m.id)).length);
      recountLedger();
    }
    window.addEventListener('zaxia:messages-read', recountMock);
    window.addEventListener('zaxia:ledger-messages-updated', (e: Event) => {
      const ids = (e as CustomEvent<{ ids: string[] }>).detail?.ids ?? [];
      if (ids.length) {
        ledgerIdsRef.current = ids;
        recountLedger();
      }
    });
    return () => window.removeEventListener('zaxia:messages-read', recountMock);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const itemRefs = useRef<Array<HTMLDivElement | null>>(new Array(SIDEBAR_ITEMS.length).fill(null));
  const { focusedIndex } = useKeyboardNavigation({
    gridColumns: 1,
    itemCount: SIDEBAR_ITEMS.length,
    enabled: enabled && !insideGreenStay && !viewIsOpen,  // Disable when inside Green Stay or when any view is open
    onRightBoundary,
    onSelect: (index) => {
      const item = SIDEBAR_ITEMS[index];
      switch (item) {
        case 'weather':
          onWeatherClick?.();
          break;
        case 'calendar':
          onCalendarClick?.();
          break;
        case 'requests':
          onRequestsClick?.();
          break;
        case 'messages':
          onMessagesClick?.();
          break;
        case 'greenStay':
          // Phase P2.2-10: Navigate to full Green Stay view
          onGreenStayClick?.();
          break;
      }
    },
  });

  // Use real bookings when available, fall back to mock calendar events
  const now = new Date();
  const nextBooking = bookings
    ? bookings
        .filter(b => new Date(b.datetime) > now && (b.status === BookingStatus.CONFIRMED || b.status === BookingStatus.PENDING))
        .sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime())[0] ?? null
    : null;
  const nextEvent = nextBooking
    ? { title: `Booking: ${nextBooking.serviceName}`, datetime: nextBooking.datetime }
    : getNextEvent();
  const focusedItem = SIDEBAR_ITEMS[focusedIndex];

  // P2.8-4b: Scroll focused sidebar item into view (prevents hidden focus in 720p TV mode)
  useEffect(() => {
    if (!enabled) return;
    const el = itemRefs.current[focusedIndex];
    if (el) requestAnimationFrame(() => el.scrollIntoView({ block: 'nearest', inline: 'nearest' }));
  }, [focusedIndex, enabled]);

  // Phase D4.3: Check if today has Green Stay preferences
  const today = new Date().toISOString().split('T')[0]!;
  const todaySkipCleaning = skipCleaningDays.includes(today);
  const todayTowelsOnly = towelsOnlyDays.includes(today) && !todaySkipCleaning;

  return (
    <aside className="sidebar-root h-full flex flex-col py-8 px-5 bg-gradient-to-b from-zaxia-dark/90 to-zaxia-dark-deep/95 border-r border-white/[0.04] overflow-hidden">
      <div className="sidebar-scroll-area flex-1 overflow-y-auto overflow-x-hidden pr-1 space-y-5" style={{ scrollbarWidth: 'thin' }}>
        {/* Weather Widget */}
        <SidebarCard
          ref={(el: HTMLDivElement | null) => { itemRefs.current[0] = el; }}
          isFocused={enabled && focusedItem === 'weather'}
          onClick={onWeatherClick}
        >
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] text-zaxia-gray/80 uppercase tracking-[0.1em] font-medium">
            Weather
          </span>
          <WeatherIcon condition={liveWeather?.current.simple ?? 'partly-cloudy'} />
        </div>
        <div className="text-3xl font-display font-semibold text-white tracking-tight">
          {liveWeather ? `${liveWeather.current.temp}°C` : '—'}
        </div>
        <div className="text-sm text-white/50 mt-1">{liveWeather?.location ?? 'Loading…'}</div>
      </SidebarCard>

      {/* Calendar Widget */}
      <SidebarCard
        ref={(el: HTMLDivElement | null) => { itemRefs.current[1] = el; }}
        isFocused={enabled && focusedItem === 'calendar'}
        onClick={onCalendarClick}
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] text-zaxia-gray/80 uppercase tracking-[0.1em] font-medium">
            My Calendar
          </span>
          <Calendar className="w-5 h-5 text-zaxia-teal/80" />
        </div>
        {/* Phase D4.3: Green Stay reminder for today */}
        {(todaySkipCleaning || todayTowelsOnly) && (
          <div className="mb-2 py-1.5 px-2 rounded-lg bg-green-900/30 border border-green-500/20">
            <p className="text-green-400 text-xs">
              {todaySkipCleaning ? '♻️ No cleaning today' : '♻️ Towels only today'}
            </p>
          </div>
        )}
        {nextEvent ? (
          <div>
            <div className="text-sm font-medium text-white/90 leading-tight">
              {nextEvent.title.replace(/^Booking: /, '')}
            </div>
            <div className="text-xs text-zaxia-teal/80 mt-1">
              {formatEventTime(nextEvent.datetime)}
            </div>
          </div>
        ) : (
          <div className="text-sm text-white/40">No upcoming events</div>
        )}
      </SidebarCard>

      {/* Phase P2.2-9: My Requests Widget */}
      <SidebarCard
        ref={(el: HTMLDivElement | null) => { itemRefs.current[2] = el; }}
        isFocused={enabled && focusedItem === 'requests'}
        onClick={onRequestsClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wrench className="w-5 h-5 text-orange-400/80" />
            <span className="text-sm text-white/90">My Room</span>
          </div>
        </div>
      </SidebarCard>

      {/* Messages Widget */}
      <SidebarCard
        ref={(el: HTMLDivElement | null) => { itemRefs.current[3] = el; }}
        isFocused={enabled && focusedItem === 'messages'}
        onClick={onMessagesClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Mail className="w-5 h-5 text-zaxia-teal/80" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
              )}
            </div>
            <span className="text-sm text-white/90">Messages</span>
          </div>
          {unreadCount > 0 && (
            <span className="bg-teal-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full animate-pulse">
              {unreadCount}
            </span>
          )}
        </div>
      </SidebarCard>

      {/* Divider */}
      <div className="sidebar-divider my-5 h-px bg-white/[0.04]" />

      {/* Green Stay Status - Phase P2.2-10 */}
      <SidebarCard
        ref={(el: HTMLDivElement | null) => { itemRefs.current[4] = el; }}
        isFocused={enabled && focusedItem === 'greenStay'}
        onClick={onGreenStayClick}
      >
        <div className="flex items-center gap-2 mb-3">
          <Leaf className="w-4 h-4 text-green-400/80" />
          <span className="text-[10px] text-zaxia-gray/80 uppercase tracking-[0.1em] font-medium">
            Green Stay
          </span>
        </div>

        {/* Status Badges */}
        <div className="space-y-2">
          {/* Eco Mode */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/60">Eco Mode</span>
            <span className={clsx(
              "px-2 py-0.5 rounded-full text-[10px] font-medium",
              greenStayEnabled
                ? "bg-green-500/20 text-green-400"
                : "bg-white/10 text-white/40"
            )}>
              {greenStayEnabled ? "ON" : "OFF"}
            </span>
          </div>

          {/* Skip Cleaning */}
          <div className="flex items-center justify-between gap-1">
            <span className="text-xs text-white/60 shrink-0">Skip Cleaning</span>
            <span className={clsx(
              "px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap shrink-0",
              skipCleaningDays.length > 0
                ? "bg-blue-500/20 text-blue-400"
                : "bg-white/10 text-white/40"
            )}>
              {skipCleaningDays.length > 0 ? `${skipCleaningDays.length} days` : "OFF"}
            </span>
          </div>

          {/* Towels Only */}
          <div className="flex items-center justify-between gap-1">
            <span className="text-xs text-white/60 shrink-0">Towels Only</span>
            <span className={clsx(
              "px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap shrink-0",
              towelsOnlyDays.length > 0
                ? "bg-purple-500/20 text-purple-400"
                : "bg-white/10 text-white/40"
            )}>
              {towelsOnlyDays.length > 0 ? `${towelsOnlyDays.length} days` : "OFF"}
            </span>
          </div>
        </div>

        {/* Total Savings Hint */}
        {greenStayEnabled && (skipCleaningDays.length > 0 || towelsOnlyDays.length > 0) && (
          <div className="mt-3 pt-3 border-t border-white/10">
            <div className="text-[10px] text-green-400/80 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              <span>View your impact →</span>
            </div>
          </div>
        )}
      </SidebarCard>
      </div>


    </aside>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

interface SidebarCardProps {
  children: React.ReactNode;
  isFocused: boolean;
  onClick?: () => void;
}

const SidebarCard = forwardRef<HTMLDivElement, SidebarCardProps>(
  function SidebarCard({ children, isFocused, onClick }, ref) {
  return (
    <motion.div
      ref={ref}
      className={clsx(
        'sidebar-widget rounded-xl p-4 mb-3 cursor-pointer keyboard-focusable',
        'bg-white/[0.03] border border-white/[0.04]',
        { 'keyboard-focused': isFocused }
      )}
      animate={{ scale: isFocused ? 1.03 : 1 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      onClick={onClick}
    >
      {children}
    </motion.div>
  );
}
);
